import { MessageDotsCircle } from "@untitled-ui/icons-react";
import { useCallback, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useLayoutContext } from "@/layouts/LayoutContext";
import { cn } from "@/lib/utils";
import { type User } from "@/types";

import { useGroupInviteLinkListener } from "../hooks";
import {
  isPrivateConversationWithUser,
  useFetchConversationOfUser,
} from "../services";
import { type Conversation } from "../types";
import MessengerListPanel from "./MessengerListPanel";
import GroupJoinPanel from "./group-join/GroupJoinPanel";
import ConversationChat from "./messages/ConversationChat";

export default function MessengerCompact() {
  const [open, setOpen] = useState(false);
  const [activeUser, setActiveUser] = useState<User<true>>();
  const [activeConversation, setActiveConversation] =
    useState<Conversation<true, true, true>>();
  const [activeGroupInviteToken, setActiveGroupInviteToken] =
    useState<string>();

  const { data: activeUserConversation } = useFetchConversationOfUser(
    activeUser?.id,
  );

  const { addHeaderButton, messengerBottomOffset, setMessengerCompactEdge } =
    useLayoutContext();
  const isDesktop = useMediaQuery("(min-width: 640px)");

  const handleClose = () => {
    setActiveConversation(undefined);
    setActiveUser(undefined);
    setActiveGroupInviteToken(undefined);
  };

  const handleGroupInvite = useCallback((token: string) => {
    setActiveGroupInviteToken(token);
    setActiveConversation(undefined);
    setActiveUser(undefined);
    setOpen(true);
  }, []);

  useGroupInviteLinkListener(handleGroupInvite);

  const privateUserConversation = isPrivateConversationWithUser(
    activeUserConversation,
    activeUser?.id,
  )
    ? activeUserConversation
    : undefined;
  const activeChat = activeConversation ?? privateUserConversation ?? undefined;

  useEffect(() => {
    addHeaderButton(
      "messenger",
      <div className="flex items-center p-2" onClick={() => setOpen(!open)}>
        <div className="relative">
          <MessageDotsCircle />
          <div className="absolute start-1 bottom-1 flex size-2 translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full bg-white">
            <div className="bg-success-500 size-1.5 rounded-full" />
          </div>
        </div>
      </div>,
    );
  }, [addHeaderButton, open]);

  const isListPanel =
    activeConversation === undefined &&
    activeUser === undefined &&
    activeGroupInviteToken === undefined;

  const content = (
    <>
      {activeGroupInviteToken ? (
        <GroupJoinPanel
          token={activeGroupInviteToken}
          open={open}
          setOpen={setOpen}
          onOpenConversation={(conversation) => {
            setActiveGroupInviteToken(undefined);
            setActiveConversation(conversation);
          }}
          onClose={() => setActiveGroupInviteToken(undefined)}
          showBackButton
        />
      ) : isListPanel ? (
        <MessengerListPanel
          open={open}
          setOpen={setOpen}
          showToggle
          onConversationClick={setActiveConversation}
          onUserClick={setActiveUser}
        />
      ) : activeChat ? (
        <ConversationChat
          open={open}
          setOpen={setOpen}
          conversation={activeChat}
          onClose={handleClose}
        />
      ) : (
        <ConversationChat
          open={open}
          setOpen={setOpen}
          user={activeUser!}
          onClose={handleClose}
          onConversationCreated={(conversation) => {
            setActiveConversation(conversation);
            setActiveUser(undefined);
          }}
        />
      )}
    </>
  );

  const collapsedHeight = 60;
  const bottomOffset = 10 + messengerBottomOffset;
  const stripBottom = messengerBottomOffset > 0 ? bottomOffset : 32;

  useEffect(() => {
    setMessengerCompactEdge(isDesktop ? stripBottom + collapsedHeight : 0);
    return () => setMessengerCompactEdge(0);
  }, [isDesktop, stripBottom, setMessengerCompactEdge]);

  return (
    <AnimateHeight
      className={cn(
        "fixed inset-0 z-50 max-h-dvh rounded-xl bg-white text-sm shadow-lg sm:inset-auto sm:end-8 sm:bottom-8 sm:max-h-[720px] sm:w-sm",
        open && "border",
        !open ? "-top-20" : "top-0",
        "overflow-hidden transition-all duration-500",
      )}
      style={
        messengerBottomOffset > 0 ? { bottom: `${bottomOffset}px` } : undefined
      }
      contentClassName="flex flex-col h-full"
      height={open ? "100%" : 60}
    >
      {content}
    </AnimateHeight>
  );
}
